'use client' // Client component

import { CacheProvider } from '@emotion/react'
import Box from '@mui/material/Box'
import CssBaseline from '@mui/material/CssBaseline'
import { ThemeProvider } from '@mui/material/styles'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { usePathname } from 'next/navigation'
import { useState } from 'react'
import { ToastContainer } from 'react-toastify'
import { AGGridThemeProvider } from '../components/AGGrid/AGGridThemeProvider'
import Sidebar from '../components/Sidebar'
import createEmotionCache from '../createEmotionCache'
import ToastStyles from '../styles/ToastStyles'
import theme from '../styles/theme'
import GlobalProvider from './providers'

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes - data is considered fresh for 5 minutes
      gcTime: 10 * 60 * 1000, // 10 minutes - cache garbage collection time (formerly cacheTime)
      retry: 1, // Only retry once on failure
      refetchOnWindowFocus: false, // Don't refetch when window regains focus
      refetchOnMount: true, // Refetch when component mounts if data is stale
    },
  },
})

export default function ClientProviders({
  children,
  nonce,
}: {
  children: React.ReactNode
  nonce?: string
}) {
  const cache = createEmotionCache(nonce)
  const pathname = usePathname() || ''

  const actualRoutes = [
    '/admin',
    '/demo',
    '/highlevelstats',
    '/homehub',
    '/kpidashboard',
    '/claimshire',
    '/myclaims',
    '/pgpartners',
    '/pgroleallocation',
    '/pguserpartner',
    '/recover-password',
    '/request',
    '/settings',
    '/uberaccident',
    '/claims',
    '/referrals',
    '/supplierrates',
    '/rates',
    '/source_rates',
    '/postcodearea',
  ]

  const hideSidebar =
    !actualRoutes.includes(pathname) &&
    !pathname.startsWith('/claims/') &&
    !pathname.startsWith('/referrals/') &&
    !pathname.startsWith('/admin/')

  const [isCollapsed, setIsCollapsed] = useState(false) // Track sidebar state

  return (
    <QueryClientProvider client={queryClient}>
      <CacheProvider value={cache}>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <AGGridThemeProvider>
            <GlobalProvider>
              {hideSidebar ? (
                <main>{children}</main>
              ) : (
                <Box sx={{ display: 'flex' }}>
                  <Sidebar
                    isCollapsed={isCollapsed}
                    setIsCollapsed={setIsCollapsed}
                  />
                  <Box
                    component="main"
                    sx={{
                      flexGrow: 1,
                      padding: { xs: 2, md: 3 },
                      marginLeft: {
                        xs: 0, // No margin for mobile
                        sm: 0, // No margin for small tablets
                        md: isCollapsed ? '80px' : '250px', // Show margin for large screens
                      },
                      width: {
                        xs: '100%',
                        sm: '100%',
                        md: `calc(100% - ${isCollapsed ? '80px' : '250px'})`,
                      },
                      transition: 'margin-left 0.3s ease', // Smooth transition
                    }}
                  >
                    {children}
                  </Box>
                </Box>
              )}
            </GlobalProvider>
          </AGGridThemeProvider>
          <ToastStyles />
          <ToastContainer
            position="bottom-right"
            autoClose={5000}
            hideProgressBar={false}
            closeOnClick
            pauseOnHover
            draggable
          />
          {/*
            To add React Query DevTools for debugging, install the package and uncomment:
            npm install @tanstack/react-query-devtools

            Then add this before the closing ThemeProvider tag:
            {process.env.NODE_ENV === 'development' && <ReactQueryDevtools initialIsOpen={false} />}
          */}
        </ThemeProvider>
      </CacheProvider>
    </QueryClientProvider>
  )
}
